import {
  CheckmarkDarkBlue,
  Miss
} from '../../../../shared/icons/src/lib/shared-icons'
import type { Cell, Row, TableProps } from '../ComparisonTable/index'
import { Themes } from '../ComparisonTable/index'
import { InformationModal } from './InformationModal'
import { PriceDisplay } from './PriceDisplay'

// This file contains the data for the monitoring plan comparison table
// It is used in the OdmonExpandableMonitoring component
// To add a new row create a new list of cells and a new row object
// Then add the row object to the tableProps.bodyRows array

const row1Cells: Cell[] = ['Compare Plans', 'Core', 'Pro', 'Pro Plus']

const row1: Row = {
  cells: row1Cells,
  category: 'header'
}

const row2Cells: Cell[] = [
  <div className="flex items-center gap-1" key="row2-cell1">
    <p>
      <strong>Professional Monitoring: </strong>
      Our highly-trained agents are ready 24/7 to request dispatch in the event
      of a break-in, fire, or medical emergency.
    </p>
    <InformationModal
      title="Professional Monitoring"
      description="Break-ins, fires, water damage, even extreme temperatures — whatever the danger, professional monitoring from SimpliSafe® will protect your home. You’ll have the peace of mind of knowing that trained security experts are keeping your home and family safe."
    />
  </div>,
  <CheckmarkDarkBlue key="row2-cell1" className="h-8 w-8" />,
  <CheckmarkDarkBlue key="row2-cell2" className="h-8 w-8" />,
  <CheckmarkDarkBlue key="row2-cell3" className="h-8 w-8" />
]

const row2: Row = {
  cells: row2Cells,
  category: 'monitoring'
}

const row3Cells: Cell[] = [
  <div className="flex items-center gap-1" key="row3-cell1">
    <p>
      <strong>SimpliSafe® Active Guard Outdoor Protection: </strong>A double
      layer of outdoor defense — advanced AI detects threats and live agents
      intervene to proactively prevent crime outside your home while your system
      is armed.
    </p>
    <InformationModal
      title="SimpliSafe® Active Guard Outdoor Protection"
      description="With a Pro or Pro Plus monitoring plan, SimpliSafe® security agents can now intervene when there’s a potential threat outside your home. All you need is the Outdoor Camera (Series 2).

      While your system is armed during plan hours, agents can use the live video feed and 2-way audio to see and speak to trespassers, or activate the camera’s loud, built-in siren and bright spotlight to drive them away — deterring crimes in progress.

      SimpliSafe® Active Guard Outdoor Protection is for residential use only and is not available in Puerto Rico."
    />
  </div>,
  <Miss key="row3-cell1" className="h-8 w-8" />,
  <div key="row3-cell2" className="flex flex-col justify-center items-center">
    <CheckmarkDarkBlue key="row3-cell2" className="h-8 w-8" />
    <strong>Available 8pm-6am, local</strong>
  </div>,
  <div key="row3-cell2" className="flex flex-col justify-center items-center">
    <CheckmarkDarkBlue className="h-8 w-8" />
    <strong>Available 24/7</strong>
  </div>
]
const row3: Row = {
  cells: row3Cells,
  category: 'monitoring'
}

const row4Cells: Cell[] = [
  <p key="row4-cell1">
    <strong>Video Verification: </strong>
    If your cameras capture video evidence during an alarm, agents will relay
    information to emergency dispatch, resulting in faster police response
    compared to unverified alarms.
  </p>,
  <CheckmarkDarkBlue key="row4-cell1" className="h-8 w-8" />,
  <CheckmarkDarkBlue key="row4-cell2" className="h-8 w-8" />,
  <CheckmarkDarkBlue key="row4-cell3" className="h-8 w-8" />
]
const row4: Row = {
  cells: row4Cells,
  category: 'mobile-app'
}

const row5Cells: Cell[] = [
  <div className="flex items-center gap-1" key="row5-cell1">
    <p>
      <strong>Intruder intervention: </strong>
      Agents can use the Smart Alarm Indoor Camera to see and speak to intruders
      to help stop crime in real time during an alarm.
    </p>
    <InformationModal
      title="Intruder Intervention"
      description="Agents can use the Smart Alarm indoor camera to see, speak to and deter intruders in real time during an alarm. Agents can only access your camera's microphone and video feed during an active alarm."
    />
  </div>,
  <CheckmarkDarkBlue key="row5-cell1" className="h-8 w-8" />,
  <CheckmarkDarkBlue key="row5-cell2" className="h-8 w-8" />,
  <CheckmarkDarkBlue key="row5-cell3" className="h-8 w-8" />
]
const row5: Row = {
  cells: row5Cells,
  category: 'mobile-app'
}

const row6Cells: Cell[] = [
  <p key="row6-cell1">
    <strong>Unlimited camera recordings </strong>
    30 day cloud storage for up to 10 cameras.
  </p>,
  <CheckmarkDarkBlue key="row6-cell1" className="h-8 w-8" />,
  <CheckmarkDarkBlue key="row6-cell2" className="h-8 w-8" />,
  <CheckmarkDarkBlue key="row6-cell3" className="h-8 w-8" />
]

const row6: Row = {
  cells: row6Cells,
  category: 'mobile-app'
}

const row7Cells: Cell[] = [
  <p key="row7-cell1">
    <strong>Control your system in the app: </strong>
    Arm, disarm, confirm or cancel alarms using your app
  </p>,
  <CheckmarkDarkBlue key="row7-cell1" className="h-8 w-8" />,
  <CheckmarkDarkBlue key="row7-cell2" className="h-8 w-8" />,
  <CheckmarkDarkBlue key="row7-cell3" className="h-8 w-8" />
]

const row7: Row = {
  cells: row7Cells,
  category: 'mobile-app'
}

const row8Cells: Cell[] = [
  <p key="row8-cell1">
    <strong>Built-in cellular connection and back-up battery: </strong>
    The Base Station includes a cellular connection that keeps you linked to
    professional monitoring and a back-up battery that lasts up to 24 hours in
    case there’s a power outage.
  </p>,
  <CheckmarkDarkBlue key="row8-cell1" className="h-8 w-8" />,
  <CheckmarkDarkBlue key="row8-cell2" className="h-8 w-8" />,
  <CheckmarkDarkBlue key="row8-cell3" className="h-8 w-8" />
]

const row8: Row = {
  cells: row8Cells,
  category: 'mobile-app'
}

const row9Cells: Cell[] = [
  <p key="row9-cell1">
    <strong>Integrates with your smart home: </strong>
    Use your voice to arm your system using Amazon Alexa or Google Assistant.
    Compatible with August Locks to lock and unlock your door when you arm or
    disarm your SimpliSafe system.
  </p>,
  <CheckmarkDarkBlue key="row9-cell1" className="h-8 w-8" />,
  <CheckmarkDarkBlue key="row9-cell2" className="h-8 w-8" />,
  <CheckmarkDarkBlue key="row9-cell3" className="h-8 w-8" />
]

const row9: Row = {
  cells: row9Cells,
  category: 'mobile-app'
}

const row10Cells: Cell[] = [
  <div className="flex items-center gap-1" key="row10-cell1">
    <p key="row10-cell1">
      <strong>Anti-Theft Guarantee: </strong>
      Get up to $500 toward your insurance deductible if you experienced loss
      from a break-in when your system was armed.
    </p>
    <InformationModal
      title="SimpliSafe® Anti-Theft Guarantee"
      description={
        <span>
          The SimpliSafe Anti-Theft Guarantee Program offers a potential
          reimbursement of up to $500 towards your homeowner&apos;s,
          renter&apos;s or commercial property insurance deductible in the event
          of a covered burglary. To be eligible, you must be the monitoring plan
          primary owner, at least 18 years old, and have an active, continually
          paid Pro, Pro Business, Pro Plus, or Pro Plus Business monitoring plan
          at the time of the alleged theft or loss, with at least one outdoor
          camera enrolled in SimpliSafe Active Guard Outdoor Protection. Your
          SimpliSafe system must have been properly set up and armed at the time
          of the loss and entry must have been through a window or door armed
          with a SimpliSafe sensor and during your SimpliSafe Active Guard
          Outdoor Protection plan hours. You must submit a complete request
          within 60 days of the burglary, including documentation of your
          accepted insurance claim and deductible payment on insurer
          letterhead. This program is not an insurance policy and SimpliSafe is
          not an insurer; it&apos;s a discretionary offering with only one
          reimbursement permitted per calendar year per monitored property, and
          SimpliSafe reserves the right to reject claims that don&apos;t meet
          all specified requirements. SimpliSafe also reserves the right to
          modify or terminate this program at any time. See{' '}
          <a href="/legal/anti-theft" target="_blank">
            Terms & Conditions
          </a>{' '}
          for full details.
        </span>
      }
    />
  </div>,
  <Miss key="row10-cell1" className="h-8 w-8" />,
  <CheckmarkDarkBlue key="row10-cell2" className="h-8 w-8" />,
  <CheckmarkDarkBlue key="row10-cell3" className="h-8 w-8" />
]

const row10: Row = {
  cells: row10Cells,
  category: 'mobile-app'
}

const row11Cells: Cell[] = [
  <p key="row11-cell1">
    <strong>No contracts. No cancellation fees: </strong>
    You’ll never be locked into a long term contract, so you can cancel any
    time.
  </p>,
  <CheckmarkDarkBlue key="row11-cell1" className="h-8 w-8" />,
  <CheckmarkDarkBlue key="row11-cell2" className="h-8 w-8" />,
  <CheckmarkDarkBlue key="row11-cell3" className="h-8 w-8" />
]

const row11: Row = {
  cells: row11Cells,
  category: 'mobile-app'
}

const row12Cells: Cell[] = [
  <span key="row12-cell1" className="text-sm font-bold block mt-3 md:text-2xl">
    Price
  </span>,
  <PriceDisplay sku="SSEDSM2__4867366" key="row12-cell2" />,
  <PriceDisplay sku="SSODMONON__13798447" key="row12-cell3" />,
  <PriceDisplay sku="SSODMON247__13798553" key="row12-cell4" />
]

const row12: Row = {
  cells: row12Cells,
  category: 'price'
}

const tableProps: TableProps = {
  headRows: [row1],
  bodyRows: [
    row2,
    row3,
    row4,
    row5,
    row6,
    row7,
    row8,
    row9,
    row10,
    row11,
    row12
  ]
}

export const TableData: TableProps = {
  ...tableProps,
  theme: Themes.planComparison
}
